// What the embedded web app does when a navigation reaches a screen the
// iPhone app draws itself (docs/ios-build-spec.md §6-7). React-router
// moves with history.pushState, and WebKit asks the app's navigation
// policy only about real page loads, never about a same-document push: a
// <Link to="/companion/chat/julia"> inside a web screen would otherwise
// open the web chat inside the app's Projects screen. So the web side
// watches its own location (NativeLinkBridge.tsx) and decides here:
//
//   web       a web screen: render it.
//   handoff   a native screen: post {type: "navigate"} (the app opens its
//             own screen over this one) and go back to the previous entry,
//             so the web screen is where the user left it. With no entry
//             behind (the screen's page was replaced), the web page that
//             was showing is put back instead.
//   external  a Safari-only path (/login-assist/<token>, the handsets'
//             screens …, nativePaths.isSafariOnlyPath): undone the same
//             way, then loaded for real once the web screen is back, so the
//             app's navigation policy sees an ordinary page load and opens
//             it in Safari. Never handed to the app as Today.
//   ignore    the same handoff, still settling: a desktop path pushes, then
//             CustomerShell's phone redirect replaces it with the companion
//             path. One navigation, one message.
//   stay      render the native screen here, as a web page, and say nothing:
//             the page's first location (what the app chose to load, maybe
//             a screen it cannot draw yet: a message would bounce it back
//             forever; also when the user steps back onto it), the app not
//             listening, nothing to put back, or the page put back
//             redirected straight here again (a blueprint page for an
//             ordinary agent sends you to its chat; putting it back again
//             would loop).
//
// A navigation is decided ONCE (createBridgeMemory): the shell asks while
// it renders, the bridge acts in its effect, and a later re-render (the
// keyboard opening, a store update) gets the same answer. Decided again,
// a "stay" whose reason had lapsed turned into a handoff nobody performs,
// and the screen went blank for good. A decision that hides the screen
// (handoff, external, ignore) lapses into "stay" after SETTLE_MS, so a
// handoff that never lands leaves the page rendered, not blank.
//
// Pure: no React, no browser globals. Self-test: linkBridge.selftest.ts.

import { resolveAppPath, splitPath } from "@/companion/nativePaths";

import type { NativeMessage } from "./embedded";

export interface BridgeLocation {
  pathname: string;
  search: string;
  hash: string;
  /** The entry's history state (react-router's `usr`), kept when the entry is put back. */
  state?: unknown;
}

export type NavigateMessage = Extract<NativeMessage, { type: "navigate" }>;

/** Where a location goes when it is not a web screen. */
export type BridgeTarget = { kind: "native"; message: NavigateMessage } | { kind: "safari"; href: string };

export interface BridgeInput {
  location: BridgeLocation;
  /** react-router's index of the current history entry (history.state.idx); null when unknown. */
  index: number | null;
  /** This is the entry the page booted on. */
  initial: boolean;
  /** The web page showing before this navigation (the last web location), to put back. */
  origin: BridgeLocation | null;
  /** A handoff is still settling (posted, not yet back on the web screen). */
  pending: boolean;
  /** The target (targetKey) the origin was just put back for, while that is recent. */
  restoredFor: string | null;
  /** Whether the app is listening (window.webkit.messageHandlers.lattice). */
  canPost: boolean;
}

export type BridgeUndo = { kind: "back" } | { kind: "replace"; to: BridgeLocation };

export type BridgeDecision =
  | { kind: "web" }
  | { kind: "stay" }
  | { kind: "ignore" }
  | { kind: "handoff"; message: NavigateMessage; then: BridgeUndo }
  | { kind: "external"; href: string; then: BridgeUndo };

const WEB: BridgeDecision = { kind: "web" };
const STAY: BridgeDecision = { kind: "stay" };
const IGNORE: BridgeDecision = { kind: "ignore" };

/**
 * Where a location goes: the app (a navigate message, with a desktop path
 * mapped the way a phone browser is redirected, resolveAppPath, so the app
 * always receives a companion location), Safari, or null for a web screen.
 */
export function bridgeTargetFor(location: BridgeLocation): BridgeTarget | null {
  const { companion, destination } = resolveAppPath(location.pathname + location.search + location.hash);
  if (destination.kind === "web") return null;
  if (destination.kind === "safari") return { kind: "safari", href: destination.path };
  const { pathname, search, hash } = splitPath(companion);
  return { kind: "native", message: { type: "navigate", path: pathname, search, hash } };
}

/** The navigate message for a location, or null when the app does not draw it. */
export function nativeMessageFor(location: BridgeLocation): NavigateMessage | null {
  const target = bridgeTargetFor(location);
  return target?.kind === "native" ? target.message : null;
}

/** One string per destination, to recognise the same handoff twice. */
export function navigateTarget(message: NavigateMessage): string {
  return message.path + message.search + message.hash;
}

export function targetKey(target: BridgeTarget): string {
  return target.kind === "native" ? navigateTarget(target.message) : target.href;
}

export function decideBridge(input: BridgeInput): BridgeDecision {
  const target = bridgeTargetFor(input.location);
  if (!target) return WEB;
  // Before the settle window: the entry the app loaded renders whenever the
  // user is on it, including when a handoff's step back lands on it.
  if (input.initial || !input.canPost) return STAY;
  if (input.pending) return IGNORE;
  const then = undoFor(input, targetKey(target));
  if (!then) return STAY;
  return target.kind === "native"
    ? { kind: "handoff", message: target.message, then }
    : { kind: "external", href: target.href, then };
}

// An entry behind this one is where the user was (a push, a replace of the
// page they had opened, a step through history): going back restores it as
// it was, its state and scroll included. Without one, the web page that was
// showing is put back, once.
function undoFor(input: BridgeInput, key: string): BridgeUndo | null {
  if (input.index !== null && input.index > 0) return { kind: "back" };
  if (!input.origin || input.restoredFor === key) return null;
  return { kind: "replace", to: input.origin };
}

/** Whether the shell renders nothing for a decision (the screen is about to leave). */
export function hidesScreen(decision: BridgeDecision): boolean {
  return decision.kind === "handoff" || decision.kind === "external" || decision.kind === "ignore";
}

// How long a handoff may take to land back on the web screen. Inside it,
// further native locations are the same navigation settling (a desktop path
// pushed, then replaced by the phone redirect), not new ones; a page put
// back that redirects to the same screen again is a loop; and a decision
// that hides the screen lapses into "stay" after it.
export const SETTLE_MS = 2_000;

/** One navigation: react-router makes a new location object for each history change, and a re-render keeps it. */
export interface BridgeNavigation extends BridgeLocation {
  key: string;
}

export interface BridgeEnv {
  now: () => number;
  /** react-router's index of the current history entry (history.state.idx), null when unknown. */
  index: () => number | null;
  /** Whether the app is listening. */
  canPost: () => boolean;
}

/** What the bridge does once a navigation has rendered. */
export type BridgeAction =
  | { kind: "none" }
  /** Post the message; on success undo the entry, on failure call release(). */
  | { kind: "post"; message: NavigateMessage; then: BridgeUndo }
  /** Undo the entry; the real load comes back as "load" when the web screen is back. */
  | { kind: "undo"; then: BridgeUndo }
  /** A real page load (location.assign), for the app's navigation policy to open in Safari. */
  | { kind: "load"; href: string };

export interface BridgeMemory {
  /** The decision for a navigation: made on first ask, the same on every later one (lapsed into "stay" when it hid the screen too long). */
  decision: (nav: BridgeNavigation) => BridgeDecision;
  /** When the screen may show again: a time while this navigation hides it, else 0. */
  hiddenUntil: (nav: BridgeNavigation) => number;
  /** The navigation rendered (the bridge's effect, once per navigation): what to do. */
  arrive: (nav: BridgeNavigation) => BridgeAction;
  /** The message could not be posted: render the screen here after all. */
  release: (nav: BridgeNavigation) => void;
}

interface Decided {
  decision: BridgeDecision;
  /** When a decision that hides the screen lapses into "stay"; Infinity for the others. */
  until: number;
}

/**
 * The bridge's memory for one page (one router). `bootKey` is the key of
 * the history entry the page loaded on, read before anything navigates
 * (react-router's own key for it: history.state.key, "default" on a fresh
 * load). Decisions are kept per navigation object (a WeakMap), so a step
 * forward or back to an entry is a new navigation, decided again.
 */
export function createBridgeMemory(bootKey: string | null, env: BridgeEnv): BridgeMemory {
  const decided = new WeakMap<BridgeNavigation, Decided>();
  let last: BridgeNavigation | null = null;
  let origin: BridgeLocation | null = null;
  let pendingUntil = 0;
  let restored: { target: string; until: number } | null = null;
  // A Safari-only path waiting for its undo to land: `index` is the history
  // entry it was pushed on (null when unknown).
  let external: { href: string; until: number; index: number | null } | null = null;

  function record(nav: BridgeNavigation): Decided {
    const known = decided.get(nav);
    if (known) return known;
    const now = env.now();
    const decision = decideBridge({
      location: nav,
      index: env.index(),
      initial: bootKey !== null && nav.key === bootKey,
      origin,
      pending: now < pendingUntil,
      restoredFor: restored && now < restored.until ? restored.target : null,
      canPost: env.canPost(),
    });
    const fresh = { decision, until: hidesScreen(decision) ? now + SETTLE_MS : Infinity };
    decided.set(nav, fresh);
    return fresh;
  }

  function decision(nav: BridgeNavigation): BridgeDecision {
    const r = record(nav);
    return env.now() < r.until ? r.decision : STAY;
  }

  // The Safari load, taken by the first arrival on which its undo has
  // landed: a page that renders, or history moved off the entry the path
  // was pushed on (a step back onto an entry this page would hand off
  // again, decided "ignore" while the handoff settles). The phone
  // redirect's replace in between keeps the index: still waiting. Past
  // the settle window it is dropped, never kept for a later navigation
  // (that would open Safari out of nowhere).
  function takeExternal(d: BridgeDecision, now: number): string | null {
    if (!external) return null;
    if (now >= external.until) {
      external = null;
      return null;
    }
    const landed =
      d.kind === "web" ||
      d.kind === "stay" ||
      (d.kind === "ignore" && external.index !== null && env.index() !== external.index);
    if (!landed) return null;
    const href = external.href;
    external = null;
    return href;
  }

  return {
    decision,
    hiddenUntil(nav) {
      const r = record(nav);
      return hidesScreen(r.decision) && env.now() < r.until ? r.until : 0;
    },
    arrive(nav) {
      // Once per navigation: StrictMode runs effects twice, and a re-render
      // of the same location is not a navigation.
      if (last === nav) return { kind: "none" };
      last = nav;
      const d = decision(nav);
      const now = env.now();
      const load = takeExternal(d, now);
      if (d.kind === "web" || d.kind === "stay") {
        // Back on a page that renders: a handoff has landed.
        pendingUntil = 0;
        if (d.kind === "web") origin = { pathname: nav.pathname, search: nav.search, hash: nav.hash, state: nav.state };
        return load ? { kind: "load", href: load } : { kind: "none" };
      }
      if (d.kind === "ignore") return load ? { kind: "load", href: load } : { kind: "none" };
      const until = now + SETTLE_MS;
      pendingUntil = until;
      const target = d.kind === "handoff" ? navigateTarget(d.message) : d.href;
      if (d.then.kind === "replace") restored = { target, until };
      if (d.kind === "handoff") return { kind: "post", message: d.message, then: d.then };
      external = { href: d.href, until, index: env.index() };
      return { kind: "undo", then: d.then };
    },
    release(nav) {
      decided.set(nav, { decision: STAY, until: Infinity });
      pendingUntil = 0;
      restored = null;
      external = null;
    },
  };
}
